'use client';

import { useEffect, useRef } from 'react';
import { useTheme } from 'next-themes';
import { useThemePreference } from '@/lib/services/profile/hooks';
import {
  parseThemeChoice,
  readStoredThemeChoice,
  type ThemeChoice
} from '@/lib/utils/theme';

/**
 * Has a choice been made ON THIS BROWSER, and what was it?
 *
 * Two stores can answer, and neither alone is sufficient — the browser gate
 * caught this the hard way, with a dark-cookie browser whose `localStorage` was
 * empty being reset to the profile's default.
 *
 * - **`localStorage`** is unambiguous but not exhaustive: it can be cleared,
 *   blocked, or simply not yet written on a browser whose theme came from the
 *   cookie the server painted from.
 * - **The COOKIE the server read for this request** (`initialChoice`) fills that
 *   gap. The mirror only ever writes it from a choice already PERSISTED to
 *   `localStorage` (VALE-1164 — before that it mirrored the effective theme, so
 *   a fresh browser minted itself a `light` cookie and this function read it back
 *   as a decision), so a cookie of `dark` or `light` records a choice that was
 *   genuinely made here — and it is the value the first frame was painted from,
 *   which is precisely why overriding it would undo a correct paint.
 *
 * `'system'` counts only when `localStorage` says so explicitly, in either
 * store. That asymmetry is what keeps the measurement honest: `'system'` is what
 * an untouched browser reads, so counting a bare one would PATCH
 * `theme: 'system'` for every flag-on user on their first load and make "chose
 * system" indistinguishable from "never opened the menu".
 *
 * The effective `theme` from `useTheme()` is deliberately NOT used for this, and
 * VALE-644 sharpened rather than retired the reason. With nothing stored, the
 * provider's `defaultTheme` is `'light'` — now unconditionally, where it used to
 * be only while the flag was unknown — so a genuinely fresh browser presents as
 * an explicit `light` chooser through `useTheme()`. That is exactly the wrong
 * answer (it cost VALE-548 a round of browser verification to see), and reading
 * the stores directly is what keeps "never opened the menu" from being recorded
 * as a deliberate pick.
 */
function localThemeChoice(
  cookieChoice: ThemeChoice | undefined
): ThemeChoice | undefined {
  return (
    readStoredThemeChoice() ??
    (cookieChoice !== undefined && cookieChoice !== 'system'
      ? cookieChoice
      : undefined)
  );
}

export interface ThemePreferenceSyncProps {
  /**
   * The choice the SERVER read from the cookie for this request, or `undefined`
   * when there was none — the same value `ValeThemeProvider` seeds `next-themes`
   * with. Passed down rather than re-read from `document.cookie` so the two can
   * never disagree about what the first frame was painted from.
   */
  initialChoice?: ThemeChoice;
}

/**
 * Owns every write of `preferences.theme`, and reconciles the stored value with
 * this browser's on mount (VALE-548). Renders nothing.
 *
 * **Why the write lives here rather than in the toggle's own click handler.**
 * `ThemeToggle` mounts in five page headers and in Storybook; giving it a
 * `useQuery` made every suite that renders a page header need a
 * `QueryClientProvider`. This component mounts in exactly one place —
 * `ValeThemeProvider`, on the authenticated (non-public) tree — where the
 * profile query provably already exists.
 *
 * **It is still only ever an explicit user choice that is written, and that is
 * structural.** This watches `theme` (the CHOICE) and never `resolvedTheme`
 * (what `system` currently evaluates to). A `system` user whose laptop flips to
 * dark at sunset moves `resolvedTheme` alone — `theme` stays `'system'`, so no
 * effect here re-runs and no PATCH is generated. The only thing that moves
 * `theme` is `setTheme`, and the toggle is its only caller.
 *
 * **This runs strictly AFTER first paint, and that is by construction, not an
 * oversight.** The theme is painted server-side from the cookie before any
 * JavaScript executes (VALE-520 · DARK-3); the profile value cannot participate
 * in first paint without the async fetch that design exists to avoid. So on a
 * genuinely new browser the seed below is a one-frame correction after
 * hydration — the honest cost of not having the value at paint time, and cheap
 * because it happens once per browser: the cookie mirror records the seeded
 * choice, and every subsequent load paints it directly.
 *
 * Three branches, and the asymmetry between them is the whole design:
 *
 * | this browser | profile | action |
 * | --- | --- | --- |
 * | chose X (see `localThemeChoice`) | X (or anything) | write X when they differ — a local choice is the more recent user action |
 * | never chose | has Y | `setTheme(Y)` — seed it; NO write, nothing was chosen here |
 * | never chose | nothing chosen | nothing at all |
 *
 * The last row is why a first visit never generates a PATCH: an untouched
 * preference must stay untouched, or the adoption query cannot tell a real
 * `system` choice from a user who never opened the menu.
 */
export function ThemePreferenceSync({
  initialChoice
}: ThemePreferenceSyncProps) {
  const { theme, setTheme } = useTheme();
  const { stored, isLoading, loaded, write } = useThemePreference();
  // Reconciliation happens once. Without the latch the seed branch would fight a
  // user who changed their mind later in the session, and the write-back branch
  // would re-fire on every profile refetch until the round trip landed.
  const reconciledRef = useRef(false);
  // The choice the server is known to hold. Tracked separately from `stored`
  // because it must advance the instant we write, so a refetch settling in
  // between cannot look like a second, different choice.
  const persistedRef = useRef<ThemeChoice | undefined>(undefined);

  useEffect(() => {
    // `loaded` as well as `isLoading`, and the difference is the whole of
    // VALE-1164's third defect: a `/profile` fetch that FAILED reports
    // `isLoading: false` with no data, and reconciling against an absent profile
    // reads as "nothing is stored" — so the browser's choice would be written on
    // every load, each write as unable to converge as the last.
    if (isLoading || !loaded) return;

    const choice = parseThemeChoice(theme);

    if (!reconciledRef.current) {
      const local = localThemeChoice(initialChoice);

      if (local !== undefined) {
        // An explicit local choice wins and is written back — it is the more
        // recent user action. `stored` reads as `system` for anyone who has
        // never chosen (the backend's read default), so this is also how an
        // existing dogfooder's pick gets captured for the first time.
        if (stored !== local) write(local);
        persistedRef.current = local;
      } else if (stored !== undefined && stored !== choice) {
        // Fresh browser, a DIFFERENT choice on the server: adopt it. `setTheme`
        // writes `localStorage` and the cookie mirror follows, so the NEXT load
        // of this browser paints it server-side with no correction at all.
        //
        // `stored !== choice` is what keeps that from manufacturing a decision
        // (VALE-1164). `stored` is never `undefined` — the backend defaults an
        // unwritten theme to the product default — so adopting unconditionally
        // meant every fresh browser called `setTheme` with the value it was
        // ALREADY rendering, changing nothing on screen and persisting a local
        // choice nobody made. From then on this browser reads as an explicit
        // chooser, and the branch above would write its value over a genuine
        // pick made on the user's other device.
        setTheme(stored);
        persistedRef.current = stored;
      } else {
        // Nothing chosen anywhere. Record what is merely in effect so the next
        // pass cannot mistake the default for a choice and write it — an
        // untouched preference must stay untouched, or the adoption query cannot
        // tell a real `system` choice from a user who never opened the menu.
        persistedRef.current = choice;
      }

      reconciledRef.current = true;
      return;
    }

    // Past reconciliation, any movement in `theme` is the toggle being used.
    if (choice !== undefined && choice !== persistedRef.current) {
      write(choice);
      persistedRef.current = choice;
    }
  }, [isLoading, loaded, stored, theme, initialChoice, write, setTheme]);

  return null;
}
